// Shared truckload-split helpers — used by the Hero estimator, the Services
// hub, the Product page, and anywhere else we add multi-load orders to the
// cart. Centralizing keeps the per-load minimum billing rules and the
// multi-delivery line-item split consistent across every entry point.
export function isLightMaterial(name: string): boolean {
  const n = name.toLowerCase()
  return (
    n.includes('bark') ||
    n.includes('compost') ||
    n.includes('topsoil') ||
    n.includes('50/50') ||
    n.includes('play chips')
  )
}
export function getFullLoad(name: string): number {
  return isLightMaterial(name) ? 15 : 12
}

// The default yardage we pre-fill in quote forms and the product detail
// page's quantity selector. Soil/Mulch/Org. products ride in light 15-yd
// truckloads and customers typically order a full load of soil or bark
// for spring projects, so we default the dropdown to 15. The 50/50 Mix
// is the exception — it's a heavier blend (sand + topsoil) that most
// homeowners use in smaller quantities for raised beds and grade work,
// so it sticks with the heavy-material default of 12. Everything else
// (rock, drain rock, quarry, decor stone) defaults to 12 — a standard
// heavy truckload.
export function getDefaultQuantity(productName: string): string {
  if (!productName) return '12'
  const n = productName.toLowerCase()
  // 50/50 Mix is light by truckload sizing but defaults to 12 yd to match
  // typical homeowner order sizes (raised beds, top-dressing).
  if (n.includes('50/50')) return '12'
  // Fill dirt removed — it's a heavy Base & Compaction material
  // (clay/subsoil density), so it ships 12 yd/load like the other
  // aggregates, not 15 yd like bark/compost/topsoil.
  const isSoilMulchOrg =
    n.includes('topsoil') ||
    n.includes('bark') ||
    n.includes('compost') ||
    n.includes('play chips')
  return isSoilMulchOrg ? '15' : '12'
}

// Minimum billable yards for a STANDALONE order (single delivery). We accept
// orders as small as 3 yd so homeowners with planter beds, paver bases, and
// small drainage repairs can use us. Anything smaller is bumped up to the
// 3 yd order minimum — trucks have fixed dispatch costs and 1–2 yd orders
// don't cover them. Partial last loads in a multi-delivery split are NOT
// bumped — if a customer is already paying for at least one full load, the
// partial is billed at its actual yardage (with the small-load surcharge
// applied per `getSmallLoadSurcharge` below).
export const MIN_LOAD = 3
// Small-load surcharge threshold per material type. Below this many yards
// in a single load, we add a surcharge to compensate for fixed dispatch
// cost. Threshold is 10 yd for ALL materials — once a customer is at or
// above 10 yd, they get base price whether the truck is light- or
// heavy-loaded. Light materials still ride in larger 15 yd loads and
// still qualify for the 5%-at-15-yd bulk discount via CartContext.
export function getSmallLoadThreshold(_productName: string): number {
  return 10
}
/**
 * Compute the small-load price surcharge multiplier for a given yardage.
 *
 * Curve:
 * - At or above threshold (12 light / 10 heavy): 1.0× (no surcharge)
 * - Linear +10% per yard short of the threshold, all the way down to the
 *   3 yd order minimum. No steeper kink below 5 yd.
 *
 * Examples (heavy, threshold 10):
 *   10 yd → 1.0×
 *    7 yd → 1.3×
 *    5 yd → 1.5×
 *    3 yd → 1.7×
 *
 * Examples (light, threshold 12):
 *   12 yd → 1.0×
 *    7 yd → 1.5×
 *    5 yd → 1.7×
 *    3 yd → 1.9×
 */
export function getSmallLoadSurcharge(
  yards: number,
  productName: string,
): number {
  const threshold = getSmallLoadThreshold(productName)
  const y = Math.max(MIN_LOAD, yards) // anything under 3 is billed as 3
  if (y >= threshold) return 1
  return 1 + (threshold - y) * 0.1
}
/**
 * Split a total yardage into per-delivery quantities.
 *
 * Rules:
 * - Loads 1..N-1 are always fullLoad yards.
 * - The last load is the actual remainder — NOT bumped to a minimum.
 *   The small-load surcharge still applies to the partial via cart pricing.
 * - For totals ≤ fullLoad, a standalone order below MIN_LOAD (3 yd) is
 *   bumped to the order minimum.
 *
 * Returns an array of yards per delivery. e.g. for non-light material:
 *   splitIntoDeliveries(16) → [12, 4]   // partial last load kept actual
 *   splitIntoDeliveries(14) → [12, 2]
 *   splitIntoDeliveries(20) → [12, 8]
 *   splitIntoDeliveries(9)  → [9]
 *   splitIntoDeliveries(2)  → [3]       // single tiny order, billed at min
 */
export function splitIntoDeliveries(
  totalYards: number,
  productName: string,
): number[] {
  const fullLoad = getFullLoad(productName)
  const total = Math.max(0, Math.round(totalYards))
  if (total <= 0) return []
  if (total <= fullLoad) {
    // Standalone single-delivery order: bump up to MIN_LOAD if below.
    return [Math.max(MIN_LOAD, total)]
  }
  const deliveries = Math.ceil(total / fullLoad)
  const lastLoad = total - (deliveries - 1) * fullLoad
  const loads: number[] = []
  for (let i = 0; i < deliveries - 1; i++) loads.push(fullLoad)
  loads.push(lastLoad)
  return loads
}
/**
 * Convenience: turn a split result into cart-line-item descriptors so callers
 * can iterate and addToCart() consistently. The product name gets a
 * "(Delivery N)" suffix when there's more than one load, matching the cart
 * drawer's per-delivery breakdown rendering.
 *
 * `requestedQuantity` is only set when the BILLED quantity exceeds the
 * material the customer actually needs — which now only happens for a
 * standalone single-delivery order under 3 yd (bumped to 3 yd minimum).
 * Multi-delivery splits no longer bump the partial last load, so billed
 * yards equal requested yards for those.
 */
export function buildDeliveryLineItems(
  totalYards: number,
  productName: string,
): { product: string; quantity: number; requestedQuantity?: number }[] {
  const total = Math.max(0, Math.round(totalYards))
  const loads = splitIntoDeliveries(totalYards, productName)
  if (loads.length === 0) return []
  if (loads.length === 1) {
    // Single-load: flag requestedQuantity only if we bumped to MIN_LOAD.
    const billed = loads[0]
    const item: {
      product: string
      quantity: number
      requestedQuantity?: number
    } = {
      product: productName,
      quantity: billed,
    }
    if (total > 0 && total < billed) item.requestedQuantity = total
    return [item]
  }
  // Multi-delivery: each load's quantity is exactly what gets billed and
  // delivered — no requestedQuantity divergence needed.
  return loads.map((qty, i) => ({
    product: `${productName} (Delivery ${i + 1})`,
    quantity: qty,
  }))
}
